import React, { useEffect, useRef } from 'react';
import { Button } from '@vibe/core';
import { format } from 'date-fns';
import type { LeaveEntry, LeaveStatus } from '../../stores/leaveStore';
import { useUsersStore } from '../../stores/usersStore';

// ─── Props ────────────────────────────────────────────────────────────────────

export interface LeaveInfoTooltipProps {
  leave: LeaveEntry;
  /** Resolved user name for the assignee */
  assigneeName: string;
  /** Currently logged-in user id */
  currentUserId: number;
  /** Whether the current viewer is an app admin */
  isAppAdmin?: boolean;
  /** Viewport position for the tooltip */
  position: { x: number; y: number };
  /** Edit leave (only available for owner when pending) */
  onEdit: (leaveId: string) => void;
  /** Recall leave by the owner (pending → recalled, approved → recalling) */
  onDelete: (leaveId: string) => void;
  /** Approve leave (only available for approver when pending) */
  onApprove: (leaveId: string) => void;
  /** Reject leave (only available for approver when pending) */
  onReject: (leaveId: string) => void;
  /** Revoke approved leave (available to approver or app admin) */
  onRevoke?: (leaveId: string) => void;
  onClose: () => void;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const STATUS_COLORS: Record<LeaveStatus, string> = {
  Pending: '#fdab3d',
  Approved: '#00c875',
  Rejected: '#d83a52',
  Recalled: '#808080',
  Recalling: '#a25ddc',
  Unapproved: '#808080',
};

// ─── Component ────────────────────────────────────────────────────────────────

const LeaveInfoTooltip: React.FC<LeaveInfoTooltipProps> = ({
  leave,
  assigneeName,
  currentUserId,
  isAppAdmin = false,
  position,
  onEdit,
  onDelete,
  onApprove,
  onReject,
  onRevoke,
  onClose,
}) => {
  const tooltipRef = useRef<HTMLDivElement>(null);
  const isPending = leave.status === 'Pending';
  const isApproved = leave.status === 'Approved';
  const isOwner = Number(leave.assigneeId) === Number(currentUserId);
  const isApprover = Number(leave.approverId) === Number(currentUserId);

  // Close tooltip on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (tooltipRef.current && !tooltipRef.current.contains(e.target as Node)) {
        onClose();
      }
    };
    const timer = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
    }, 0);
    return () => {
      clearTimeout(timer);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [onClose]);

  // Close on Escape
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  // Close on mouse leave with a short delay (allows moving to buttons inside tooltip)
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const handleMouseLeave = () => {
    closeTimerRef.current = setTimeout(() => onClose(), 400);
  };
  const handleMouseEnter = () => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  };
  useEffect(() => {
    return () => {
      if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    };
  }, []);

  // Adjust position to stay within viewport
  useEffect(() => {
    if (!tooltipRef.current) return;
    const el = tooltipRef.current;
    const rect = el.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    if (rect.right > vw - 8) {
      el.style.left = `${vw - rect.width - 8}px`;
    }
    if (rect.bottom > vh - 8) {
      el.style.top = `${vh - rect.height - 8}px`;
    }
  }, [position]);

  const dateRange = leave.startDate === leave.endDate
    ? format(new Date(leave.startDate), 'MMM d, yyyy')
    : `${format(new Date(leave.startDate), 'MMM d')} \u2192 ${format(new Date(leave.endDate), 'MMM d, yyyy')}`;

  const dayTypeLabel =
    leave.dayType === 'full' ? 'Full day' : leave.dayType === 'am' ? 'AM only' : 'PM only';

  return (
    <div
      ref={tooltipRef}
      className="rp-task-info-tooltip"
      style={{ top: position.y, left: position.x }}
      onMouseLeave={handleMouseLeave}
      onMouseEnter={handleMouseEnter}
    >
      {/* Leave type name */}
      <div className="rp-task-info-tooltip__name">{leave.leaveType}</div>

      {/* Attributes */}
      <div className="rp-task-info-tooltip__attrs">
        <div className="rp-task-info-tooltip__attr">
          <span className="rp-task-info-tooltip__attr-label">Employee</span>
          <span className="rp-task-info-tooltip__attr-value">{assigneeName}</span>
        </div>

        <div className="rp-task-info-tooltip__attr">
          <span className="rp-task-info-tooltip__attr-label">Status</span>
          <span className="rp-task-info-tooltip__attr-value">
            <span
              className="rp-leave-status-badge rp-leave-status-badge--small"
              style={{ backgroundColor: STATUS_COLORS[leave.status] }}
            >
              {leave.status}
            </span>
          </span>
        </div>

        <div className="rp-task-info-tooltip__attr">
          <span className="rp-task-info-tooltip__attr-label">Dates</span>
          <span className="rp-task-info-tooltip__attr-value">{dateRange}</span>
        </div>

        <div className="rp-task-info-tooltip__attr">
          <span className="rp-task-info-tooltip__attr-label">Day Type</span>
          <span className="rp-task-info-tooltip__attr-value">{dayTypeLabel}</span>
        </div>

        {leave.note && (
          <div className="rp-task-info-tooltip__attr">
            <span className="rp-task-info-tooltip__attr-label">Note</span>
            <span className="rp-task-info-tooltip__attr-value">{leave.note}</span>
          </div>
        )}

        {leave.createdByUserId && String(leave.createdByUserId) !== String(leave.assigneeId) && (
          <div className="rp-task-info-tooltip__attr">
            <span className="rp-task-info-tooltip__attr-label">Created by</span>
            <span className="rp-task-info-tooltip__attr-value">
              {useUsersStore.getState().users.find((u) => String(u.id) === String(leave.createdByUserId))?.name ?? `User #${leave.createdByUserId}`}
            </span>
          </div>
        )}
      </div>

      {/* Owner actions — Edit (pending only) & Recall (pending or approved).
          Recall on an approved leave transitions it to "Recalling" and waits
          for approver consent; recall on a pending leave is immediate. */}
      {isOwner && (isPending || isApproved) && (
        <div className="rp-task-info-tooltip__actions">
          {isPending && (
            <Button
              kind={Button.kinds.SECONDARY}
              size={Button.sizes.SMALL}
              onClick={() => onEdit(leave.id)}
            >
              Edit
            </Button>
          )}
          <Button
            kind={Button.kinds.PRIMARY}
            size={Button.sizes.SMALL}
            color={Button.colors.NEGATIVE}
            onClick={() => onDelete(leave.id)}
          >
            Recall
          </Button>
        </div>
      )}

      {/* Approver actions — Approve & Reject (only when pending) */}
      {isPending && isApprover && !isOwner && (
        <div className="rp-task-info-tooltip__actions">
          <Button
            kind={Button.kinds.PRIMARY}
            size={Button.sizes.SMALL}
            color={Button.colors.POSITIVE}
            onClick={() => onApprove(leave.id)}
          >
            Approve
          </Button>
          <Button
            kind={Button.kinds.PRIMARY}
            size={Button.sizes.SMALL}
            color={Button.colors.NEGATIVE}
            onClick={() => onReject(leave.id)}
          >
            Reject
          </Button>
        </div>
      )}

      {/* Revoke — approver or app admin can revoke an approved leave directly
          (skips the recalling consent step). Hidden from the owner, who uses
          Recall instead. */}
      {isApproved && !isOwner && (isApprover || isAppAdmin) && onRevoke && (
        <div className="rp-task-info-tooltip__actions">
          <Button
            kind={Button.kinds.PRIMARY}
            size={Button.sizes.SMALL}
            color={Button.colors.NEGATIVE}
            onClick={() => onRevoke(leave.id)}
          >
            Revoke
          </Button>
        </div>
      )}
    </div>
  );
};

export default LeaveInfoTooltip;
